/**
 * Dynamic upsell banner that appears on à la carte checkout pages
 * for heavy Basic-tier users. Backend (`/api/billing/upsell-check`)
 * does the math against the user's REAL 12-month payment history;
 * we just render a tight, honest "you'd save $X" pitch.
 *
 * Renders null in these cases (the API tells us):
 *   - User not logged in
 *   - User already on Pro / Kennel Elite
 *   - User's à la carte spend below $99/year (no real savings story)
 *
 * Why client-side fetch instead of server-rendering: keeps the
 * checkout pages dumb. Any page that shows this banner just renders
 * <ProUpsellBanner /> — the component figures out everything else.
 *
 * Drop-in on: DogForm.jsx, LitterForm.jsx, MarketplaceListingDialog,
 * UpcomingLitterDraftDialog, the certificate-purchase dialog, etc.
 */
import { isNativeApp } from "@/lib/nativeBridge";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { SparkleIcon as Sparkle, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";

export default function ProUpsellBanner({ context = "checkout", compact = false }) {
  // App Store 3.1.1 — no subscription upsells/prices in the native shell.
  const native = isNativeApp();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (native || !user) return;
    let cancelled = false;
    api
      .get("/billing/upsell-check")
      .then((r) => {
        if (!cancelled && r.data?.show) setData(r.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (native || !data || !data.show) return null;

  const moneyFmt = (n) =>
    n >= 1000
      ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
      : `$${n.toFixed(0)}`;

  // Two narrative variants depending on whether they've already
  // exceeded the Pro annual price:
  //   (a) Already exceeded → "Pro would have SAVED you $X"
  //   (b) Not yet exceeded → "You'll break even at month N of Pro"
  const alreadyOver = data.would_save_annual > 0;
  const headline = alreadyOver
    ? `Pro would have saved you ${moneyFmt(data.would_save_annual)} this year`
    : `You'd break even on Pro after ${Math.ceil(
        data.months_to_breakeven,
      )} months`;
  const body = alreadyOver
    ? `You've spent ${moneyFmt(data.spend_usd)} on ${data.n_payments} à la carte purchase${
        data.n_payments === 1 ? "" : "s"
      } in the last 12 months. Breeder Pro at $420/yr (or $44/mo) covers all of that plus gives you unlimited registrations, free pedigree certificates, a 9% platform fee, and the Verified Pro wax-seal — for less than you're already paying.`
    : `You've spent ${moneyFmt(data.spend_usd)} on à la carte fees so far. Once you pass ${moneyFmt(
        44 * 12,
      )} a year, Pro pays for itself — plus you'd get the Verified Pro wax-seal, free pedigree certificates, and a 9% platform fee on every sale starting day one.`;

  if (compact) {
    return (
      <Link
        to={data.checkout_url}
        className="block rounded-md border border-gold/40 bg-gold/5 hover:bg-gold/10 px-3 py-2 text-[12px] text-ink transition-colors"
        data-testid={`pro-upsell-${context}-compact`}
      >
        <Sparkle size={12} weight="fill" className="inline mr-1 text-gold" />
        <strong>{headline}.</strong> Switch to Pro →
      </Link>
    );
  }

  return (
    <div
      className="rounded-xl border border-gold/40 bg-gold/5 p-4 md:p-5 flex items-start gap-3"
      data-testid={`pro-upsell-${context}`}
    >
      <Sparkle
        size={22}
        weight="duotone"
        className="text-gold flex-shrink-0 mt-0.5"
      />
      <div className="flex-1">
        <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#8A6A00] mb-1">
          {alreadyOver ? "Smart-money tip" : "Heads up"}
        </p>
        <p className="text-sm font-medium text-ink leading-snug">{headline}.</p>
        <p className="text-[13px] text-ink/80 leading-relaxed mt-1">{body}</p>
        <Link
          to={data.checkout_url}
          className="inline-flex items-center gap-1 mt-2 text-[12px] font-bold text-ink hover:text-gold"
          data-testid={`pro-upsell-${context}-cta`}
        >
          Switch to Pro · $44/mo (or $420/yr · save $108){" "}
          <ArrowRight size={12} weight="bold" />
        </Link>
      </div>
    </div>
  );
}
